// Page docs (structure + permissions + flows): pages/events/README.md
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import Head from "next/head";
import { Box, Container, Typography, Chip, FormControlLabel, Checkbox, Select, MenuItem, TextField, InputAdornment, Divider, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from "@mui/material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import { useRouter } from "next/router";
import SearchIcon from "@mui/icons-material/Search";
import TableChartIcon from "@mui/icons-material/TableChart";
import NavBar from "@/components/Importable/Navigation";
import SectionFooter from "@/components/CreateForm/Footer";
import SubscriptionBanner from "@/components/Banners/SubscriptionBanner";
import { fetchAllEventsWithIssues } from "@/data/eventsData";
import { api, useGetEventsQuery, useGetMyEventSubmissionsQuery, useGetEventSubmissionTasksQuery, useDiscardEventSubmissionMutation } from "@/src/services/api";
import { useAppDispatch } from "@/src/store/hooks";
import { skipToken } from "@reduxjs/toolkit/query";
import { apiEventToListItem } from "@/utils/apiMappers";
import { categoryColors, green, gray, common } from "@/theme/colors";
import { getAllNewlyAdded, mergeNewlyAdded, setDbVersion, setCreatePrefill, clearCreatePrefill, removeRejectedById, getIndexCache, setIndexCache, getUserPresenceMemo, setUserPresenceMemo } from "@/utils/localStore";
import { normalizeSearchText } from "@/utils/searchText";
import { assetUrl } from "@/utils/asset";
import { preloadImages } from "@/utils/imageCache";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useDataVersionCheck } from "@/hooks/useDataVersionCheck";
import { useTagPreferences } from "@/hooks/useTagPreferences";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { openAccessGateModal } from "@/utils/accessGate";
import { useProgressiveLoading } from "@/hooks/useProgressiveLoading";
import EventCardSkeleton from "@/components/Skeleton/EventCardSkeleton";
import PrioritizeSavedSwitch from "@/components/Importable/PrioritizeSavedSwitch";
import { useGetPromotionsQuery } from "@/src/services/api";
import { SingleFeaturedBanner, SingleFeaturedBannerSkeleton } from "@/components/Importable/FeaturedBanner";
import { EventCard, isEventPast, sanitizeEventTypes } from "@/components/Importable/Cards";
import IndexPageHeader from "@/components/Importable/IndexPageHeader";
import { useIndexLayout } from "@/hooks/useIndexLayout";
import FilterBar from "@/components/OrganizationSections/FilterBar";
import IndexPanelSurface, { panelPageSx } from "@/components/Importable/IndexPanelSurface";
import { MAIN_CONTENT_ID } from "@/components/Importable/SkipLink";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const MainContainer = styled(Box)(() => ({
  display: "flex",
  minHeight: "100vh",
  overflowX: "hidden",
  width: "100%",
  maxWidth: "100vw",
  backgroundColor: alpha(gray.light, 0.1)
}));
const ContentWrapper = styled(Box)(({
  theme
}) => ({
  flexGrow: 1,
  marginLeft: 0,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: "hidden",
  maxWidth: "100%",
  width: "100%",
  backgroundColor: "transparent"
}));
const StyledContainer = styled(Container)(({
  theme
}) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  backgroundColor: "transparent",
  [theme.breakpoints.down("md")]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  [theme.breakpoints.down("sm")]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  }
}));
const EventsPage = ({
  events,
  issues
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const cacheStale = useDataVersionCheck("events");
  // Source of truth for current events list (includes local newly-added)
  // Read once on mount so we know whether to skip the API query, but allow
  // a brief retry window because `getIndexCache` returns null until the
  // module-level `hydrated` flag flips after the initial paint.
  const [cachedEvents, setCachedEvents] = useState(null);
  // Always start from SSR props so the first render matches server HTML
  // (avoids hydration mismatch when in-memory cache survives HMR).
  const [allEvents, setAllEvents] = useState(events);
  const [filteredEvents, setFilteredEvents] = useState(events);

  // Hydrate from index cache after mount (SSR-safe). Try immediately, then
  // retry on the next macrotask in case localStore's hydration timer hadn't
  // fired yet on the first attempt.
  //
  // The cache is treated as authoritative for the *count*: if the cached
  // list contains as many items as we currently show (or more), it always
  // overwrites. This prevents a race where the newly-added merge effect
  // first writes a singular item against an empty base before the cache
  // hydrates, leaving the page stuck rendering only that one event after
  // back-navigation from the detail page.
  useEffect(() => {
    const apply = () => {
      const c = getIndexCache("events");
      if (c?.length) {
        setCachedEvents(c);
        setAllEvents(prev => c.length >= prev.length ? c : prev);
        setFilteredEvents(prev => c.length >= prev.length ? c : prev);
        return true;
      }
      return false;
    };
    if (apply()) return;
    const t = setTimeout(apply, 0);
    return () => clearTimeout(t);
  }, []);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTags, setSelectedTags] = useState([]);
  const {
    prefs: tagPrefs
  } = useTagPreferences();
  const theme = useTheme();
  const router = useRouter();
  const {
    settings: siteSettings
  } = useSiteSettings();
  useEffect(() => {
    if (!siteSettings.eventsVisible) void router.replace("/organizations");
  }, [siteSettings.eventsVisible, router]);
  const {
    isAuthenticated,
    token,
    user
  } = useAuth();
  const canCreateEvent = user?.role === "admin";
  const [issuesOpen, setIssuesOpen] = useState(false);
  const {
    items: savedEvents
  } = useSavedItems("event");
  const [prioritizeSaved, setPrioritizeSaved] = useState(false);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [eventSearch, setEventSearch] = useState("");
  const autoToggledPastRef = useRef(false);
  const [sortBy, setSortBy] = useState("date");
  const [eventFormat, setEventFormat] = useState("all");
  const hasSavedEvents = savedEvents.length > 0;

  // Set of saved event slugs/IDs for border highlighting and prioritization
  const savedIdSet = useMemo(() => {
    const s = new Set();
    savedEvents.forEach(item => {
      const slug = String(item.resourceSlug ?? "").trim().toLowerCase();
      const id = String(item.resourceId ?? "").trim().toLowerCase();
      if (slug) s.add(slug);
      if (id) s.add(id);
    });
    return s;
  }, [savedEvents]);

  // ── Progressive loading ──
  // Apply past-event filter, then sort, then optional prioritize-saved.
  const displayEvents = useMemo(() => {
    // 1. Hide past events unless the toggle is on.
    let list = showPastEvents ? filteredEvents : filteredEvents.filter(e => !isEventPast(e));

    // 1b. Apply the Event Format filter (in-person / virtual / hybrid).
    if (eventFormat !== "all") {
      list = list.filter(e => {
        const hybrid = Boolean(e.isHybrid);
        if (eventFormat === "hybrid") return hybrid;
        if (eventFormat === "virtual") return e.isVirtual && !hybrid;
        // in-person
        return !e.isVirtual && !hybrid;
      });
    }

    // 2. Apply Sort By selection.
    const eventTime = e => {
      const t = e.date ? new Date(e.date).getTime() : NaN;
      return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
    };
    list = [...list].sort((a, b) => {
      if (sortBy === "az") {
        return String(a.header || "").localeCompare(String(b.header || ""));
      }
      if (sortBy === "za") {
        return String(b.header || "").localeCompare(String(a.header || ""));
      }
      // Default: by date ascending. When showing past events too, push
      // past events to the end (most recent first) so upcoming come
      // first sorted earliest-to-latest.
      const aPast = isEventPast(a);
      const bPast = isEventPast(b);
      if (aPast !== bPast) return aPast ? 1 : -1;
      const aT = eventTime(a);
      const bT = eventTime(b);
      return aPast ? bT - aT : aT - bT;
    });

    // 3. Optionally float saved events to the top.
    if (!prioritizeSaved || savedIdSet.size === 0) return list;
    return [...list].sort((a, b) => {
      const aSlug = String(a.slug || "").trim().toLowerCase();
      const aId = String(a.id || "").trim().toLowerCase();
      const aSaved = aSlug && savedIdSet.has(aSlug) || aId && savedIdSet.has(aId);
      const bSlug = String(b.slug || "").trim().toLowerCase();
      const bId = String(b.id || "").trim().toLowerCase();
      const bSaved = bSlug && savedIdSet.has(bSlug) || bId && savedIdSet.has(bId);
      if (aSaved && !bSaved) return -1;
      if (!aSaved && bSaved) return 1;
      return 0;
    });
  }, [filteredEvents, prioritizeSaved, savedIdSet, showPastEvents, sortBy, eventFormat]);

  // Auto-enable "Show Past Events" on initial load if there are no upcoming events.
  useEffect(() => {
    if (autoToggledPastRef.current) return;
    if (allEvents.length === 0) return;
    const hasUpcoming = allEvents.some(e => !isEventPast(e));
    if (!hasUpcoming) {
      setShowPastEvents(true);
      autoToggledPastRef.current = true;
    }
  }, [allEvents]);

  // On deployed environments, replace static data with live DB data
  const {
    data: apiEvents,
    isError: eventsError
  } = useGetEventsQuery(undefined, {
    skip: (cachedEvents?.length ?? 0) > 0 && !cacheStale
  });

  // Settle progressive loading once data has arrived so an empty displayEvents
  // (e.g. all events are past and the toggle is off) stops showing skeletons.
  const dataSettled = allEvents.length > 0 || apiEvents !== undefined;
  const {
    visibleItems: visibleEvents,
    showSkeletons,
    skeletonCount
  } = useProgressiveLoading(displayEvents, 10, eventsError || dataSettled);

  // ── Submission tracking (pending / rejected) ──
  // Most signed-in users have never submitted an event; once a session
  // has settled that answer as "none", the memo skips /mine entirely
  // until one of the user's own submission mutations clears it (or the
  // 7-day TTL heals any cross-device drift).
  const submissionsMemo = useMemo(() => user?.id ? getUserPresenceMemo("event-submissions", user.id) : null, [user?.id]);
  const {
    data: myEventSubmissionsData
  } = useGetMyEventSubmissionsQuery(undefined, {
    skip: !token || submissionsMemo === false
  });
  const myEventSubmissions = useMemo(() => myEventSubmissionsData ?? [], [myEventSubmissionsData]);
  useEffect(() => {
    if (!user?.id || myEventSubmissionsData === undefined) return;
    setUserPresenceMemo("event-submissions", user.id, myEventSubmissionsData.length > 0);
  }, [myEventSubmissionsData, user?.id]);
  const [discardEventSubmission] = useDiscardEventSubmissionMutation();
  const [rejectedModalOpen, setRejectedModalOpen] = useState(false);
  const userSubmissions = useMemo(() => {
    if (!user?.id) return [];
    return myEventSubmissions.filter(s => s.submittedByUserId === user.id);
  }, [myEventSubmissions, user?.id]);
  const {
    pendingSubmissions,
    rejectedSubmissions
  } = useMemo(() => {
    const keyFor = s => {
      const p = s?.payload || {};
      const history = Array.isArray(p.submissionHistory) ? p.submissionHistory : [];
      return String(history[0] || p.rootSubmissionId || p.previousSubmissionId || p.slug || p.id || p.header || s.id);
    };
    const grouped = new Map();
    userSubmissions.forEach(s => {
      const key = keyFor(s);
      const existing = grouped.get(key);
      if (!existing || new Date(s.updatedAt).getTime() >= new Date(existing.updatedAt).getTime()) grouped.set(key, s);
    });
    const latest = Array.from(grouped.values());
    return {
      pendingSubmissions: latest.filter(s => s.status?.toLowerCase() === "pending"),
      rejectedSubmissions: latest.filter(s => s.status?.toLowerCase() === "rejected")
    };
  }, [userSubmissions]);
  const latestPendingSubmission = useMemo(() => {
    if (!pendingSubmissions.length) return null;
    return [...pendingSubmissions].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
  }, [pendingSubmissions]);
  const latestRejectedSubmission = useMemo(() => {
    if (!rejectedSubmissions.length) return null;
    return [...rejectedSubmissions].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
  }, [rejectedSubmissions]);
  const activeSubmission = latestPendingSubmission || latestRejectedSubmission || null;
  const activeSubmissionId = activeSubmission?.id ?? null;
  const {
    data: rejectedTasks = []
  } = useGetEventSubmissionTasksQuery(activeSubmissionId && token ? activeSubmissionId : skipToken);
  const latestAdminMessage = useMemo(() => {
    const adminMessages = rejectedTasks.flatMap(edit => (edit.messages || []).filter(msg => msg.authorRole === "admin"));
    if (!adminMessages.length) return null;
    return adminMessages.reduce((latest, msg) => new Date(msg.createdAt).getTime() >= new Date(latest.createdAt).getTime() ? msg : latest);
  }, [rejectedTasks]);
  const pendingCount = pendingSubmissions.length;
  const rejectedCount = rejectedSubmissions.length;
  const hasPendingForUser = pendingCount > 0 && isAuthenticated;
  const hasRejectedForUser = !hasPendingForUser && rejectedCount > 0 && isAuthenticated;
  const submissionStatusLabel = hasPendingForUser ? pendingCount === 1 ? "1 submission pending" : `${pendingCount} submissions pending` : rejectedCount === 1 ? "1 submission rejected" : `${rejectedCount} submissions rejected`;
  useEffect(() => {
    if (!apiEvents) return;
    const mapped = apiEvents.filter(e => e.isActive && e.approvalStatus === "approved").map(apiEventToListItem).filter(e => e !== null);
    const newly = getAllNewlyAdded("event").filter(a => a.operation !== "deactivate");
    const merged = newly.length ? mergeNewlyAdded(mapped, newly, {
      markNew: true
    }) : mapped;
    setAllEvents(merged);
    setIndexCache("events", merged);
  }, [apiEvents]);

  // Warm the browser's HTTP image cache as soon as the event list arrives so
  // that when EventCards render (or re-render after back-navigation) the
  // images are already cached and display instantly without a loading flash.
  useEffect(() => {
    if (!allEvents.length) return;
    preloadImages(allEvents.map(e => assetUrl(e.image)));
  }, [allEvents]);

  // Surface unresolved import issues whenever they arrive.
  useEffect(() => {
    if (issues && issues.length) {
      setIssuesOpen(true);
    }
  }, [issues]);

  // Background merge of newlyAdded events. Waits until we have a non-empty
  // baseline (cached list or SSR events) before writing — otherwise an early
  // mount would replace the eventually-arriving cached list with just the
  // singular newly-added item and the page would render only that one event
  // after back-navigation from a detail page.
  useEffect(() => {
    const base = cachedEvents && cachedEvents.length ? cachedEvents : events.length ? events : null;
    if (!base) return;
    const newly = getAllNewlyAdded("event").filter(a => a.operation !== "deactivate");
    if (!newly.length) return;
    const merged = mergeNewlyAdded(base, newly, {
      markNew: true
    });
    setAllEvents(merged);
    const newest = newly.reduce((acc, cur) => cur.approvalTimestamp > acc.approvalTimestamp ? cur : acc);
    const day = new Date(newest.approvalTimestamp).toISOString().slice(0, 10);
    setDbVersion(`${day}:${newest.id}`);
  }, [cachedEvents, events]);

  // ── Featured events (promoted) ──
  const {
    data: featuredEventPromos,
    isLoading: loadingPromos
  } = useGetPromotionsQuery("featured_event");
  // Refetch promotions when the server bumps the "promotions" data
  // version — without this hook the page has no promotions listener at
  // all (SSE bumps are entity-filtered), so cached featured rows could
  // outlive an admin change until the token-based check caught up.
  const promoDispatch = useAppDispatch();
  useDataVersionCheck("promotions", useCallback(() => {
    promoDispatch(api.util.invalidateTags(["Promotions"]));
  }, [promoDispatch]));
  const {
    data: apiEventsForFeatured,
    isLoading: loadingEventsForFeatured
  } = useGetEventsQuery();
  // Promotions-first: the featured skeleton holds only while promotions
  // are in flight, or when promotions EXIST and their events are still
  // loading. An empty promotion list drops straight to the hero instead
  // of holding the skeleton for the whole events fetch.
  const loadingFeatured = loadingPromos || !!featuredEventPromos && featuredEventPromos.length > 0 && loadingEventsForFeatured;
  const featuredEventItems = useMemo(() => {
    if (!featuredEventPromos || featuredEventPromos.length === 0) return [];
    if (!apiEventsForFeatured) return [];
    const evtMap = new Map(apiEventsForFeatured.map(e => [e.id, e]));
    return featuredEventPromos.map(promo => {
      const evt = evtMap.get(promo.targetId);
      if (!evt) return null;
      const p = evt.payload || {};
      // Skip past events
      if (isEventPast(p)) return null;
      return {
        id: evt.id,
        image: assetUrl(p.image || "/img/bg7.jpg"),
        name: p.header || evt.title || "Featured Event",
        description: p.description || evt.description || "",
        link: `/events/${evt.id || evt.slug}`,
        price: p.price,
        currency: p.currency
      };
    }).filter(Boolean);
  }, [featuredEventPromos, apiEventsForFeatured]);
  const handleSidebarToggle = useCallback(() => {
    setSidebarOpen(!sidebarOpen);
  }, [sidebarOpen]);
  const handleEventClick = eventSlugOrId => {
    if (!eventSlugOrId || typeof eventSlugOrId !== "string") return;
    router.push(`/events/${eventSlugOrId}`);
  };
  const handleCreateClick = useCallback(() => {
    if (!isAuthenticated) {
      openAccessGateModal({
        reason: "login-required",
        returnUrl: "/events/create"
      });
      return;
    }
    if (!canCreateEvent) {
      // The button always renders now — signed-in accounts without
      // event-creation rights get the contact-support gate.
      openAccessGateModal({
        reason: "insufficient-role",
        returnUrl: "/events/create"
      });
      return;
    }
    if (hasPendingForUser || hasRejectedForUser) {
      setRejectedModalOpen(true);
      return;
    }
    router.push("/events/create");
  }, [isAuthenticated, canCreateEvent, router, hasPendingForUser, hasRejectedForUser]);
  const handleReviewRejected = useCallback(() => {
    if (!activeSubmission) return;
    setCreatePrefill({
      type: "event",
      payload: activeSubmission.payload,
      reason: latestAdminMessage?.body || undefined,
      submissionId: activeSubmission.id
    });
    setRejectedModalOpen(false);
    router.push("/events/create");
  }, [activeSubmission, latestAdminMessage, router]);
  const handleCreateNew = useCallback(() => {
    clearCreatePrefill();
    setRejectedModalOpen(false);
    router.push("/events/create");
  }, [router]);
  const handleDiscardSubmission = useCallback(async () => {
    if (!activeSubmission) return;
    try {
      await discardEventSubmission(activeSubmission.id).unwrap();
      clearCreatePrefill();
      removeRejectedById("event", activeSubmission.id);
      setRejectedModalOpen(false);
    } catch (error) {
      console.error("Failed to discard submission:", error);
    }
  }, [activeSubmission, discardEventSubmission]);
  const applyFilters = useCallback((categoryValue, tags = []) => {
    const normalized = String(categoryValue || "all").toLowerCase();
    let next = allEvents;
    if (normalized !== "all" && normalized.length) {
      next = next.filter(event => {
        const cats = String(event.category || "").split(" | ").map(c => c.trim().toLowerCase());
        return cats.includes(normalized);
      });
    }
    if (tags.length > 0) {
      const lowerTags = tags.map(t => t.toLowerCase());
      next = next.filter(event => lowerTags.every(t => (event.tags || []).some(et => et.toLowerCase() === t)));
    }
    // Free-text search across the fields a visitor would search by.
    // The past/upcoming split is applied downstream from
    // showPastEvents, so searching automatically stays within
    // whichever set is currently displayed.
    const term = normalizeSearchText(eventSearch.trim());
    if (term) {
      next = next.filter(event => {
        const haystack = normalizeSearchText([event.header, event.subheader, event.description, event.category, event.location, event.organization?.name, (event.tags || []).join(" ")].filter(Boolean).join(" "));
        return haystack.includes(term);
      });
    }
    setFilteredEvents(next);
  }, [allEvents, eventSearch]);

  // Re-apply filters whenever allEvents changes (cache/API data arrives)
  useEffect(() => {
    applyFilters(selectedCategory, selectedTags);
    // applyFilters changes identity when allEvents or the search term
    // changes, so this covers both.
  }, [applyFilters]);
  const handleCategoryFilter = category => {
    const target = category || "all";
    setSelectedCategory(target);
    applyFilters(target, selectedTags);
  };
  const toggleTag = useCallback(tag => {
    setSelectedTags(prev => {
      const next = prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag];
      applyFilters(selectedCategory, next);
      return next;
    });
  }, [applyFilters, selectedCategory]);
  const categories = useMemo(() => {
    // Only valid event types become filter chips — false types (e.g. a
    // stray "in-person event" value) are sanitized out.
    const unique = Array.from(new Set(allEvents.flatMap(e => sanitizeEventTypes(e.category))));
    return ["all", ...unique];
  }, [allEvents]);
  useEffect(() => {
    applyFilters(selectedCategory, selectedTags);
  }, [applyFilters, selectedCategory, selectedTags]);
  const formatDate = dateString => {
    if (!dateString) return "";
    const isoLike = /\d{4}-\d{2}-\d{2}$/.test(dateString) ? `${dateString}T00:00:00.000Z` : dateString;
    const parsed = new Date(isoLike);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC"
    });
  };
  const formatPrice = (price, currency) => {
    const p = Number.isFinite(price) ? price : 0;
    const cur = typeof currency === "string" && currency.trim() ? currency.trim() : "USD";
    if (p === 0) return "Free";
    return `${cur === "USD" ? "$" : cur}${p}`;
  };
  const truncate = (text, max = 135) => {
    if (!text) return "";
    const clean = String(text);
    return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
  };

  // Map an event category to its brand color
  const getCategoryColor = category => {
    const first = String(category || "").split(" | ")[0].trim().toLowerCase();
    return categoryColors[first] || categoryColors.default;
  };
  const renderEventCard = (event, keyPrefix = "") => {
    const eventSlug = String(event.slug || "").trim().toLowerCase();
    const eventId = String(event.id || "").trim().toLowerCase();
    const isSaved = !!eventSlug && savedIdSet.has(eventSlug) || !!eventId && savedIdSet.has(eventId);
    return /*#__PURE__*/_jsx(EventCard, {
      event: event,
      isSaved: isSaved,
      variant: eventViewMode,
      onClick: e => handleEventClick(e.id || e.slug)
    }, `${keyPrefix}${event.id}`);
  };

  // The green hero box only renders when neither the loading skeleton nor a
  // featured banner is shown. The admin action buttons live in its bottom-right
  // corner; when a featured banner takes its place we fall back to the
  // category-filter bar so admins never lose access to them.
  const indexLayout = useIndexLayout("events");
  // The green hero IS style 3. Under styles 1 and 2 the shared header
  // takes its place; the featured banner still supersedes it either way.
  const hasFeaturedBanner = indexLayout.showFeatured && !loadingFeatured && featuredEventItems.length > 0;
  const showHeroBox = indexLayout.style === 3 && !loadingFeatured && !hasFeaturedBanner;
  // The shared dynamic bar is the only filter bar now — the legacy
  // events bar (and its admin select) was retired per owner feedback.
  const useSharedFilterBar = true;
  // List is the default presentation (matches the other indexes).
  const [eventViewMode, setEventViewMode] = useState("list");
  // Styles 1/2 follow the organizations order — the header is always
  // present and the featured banner sits BELOW it, rather than replacing
  // it the way the style-3 hero does. Gating this on "no featured" was
  // what wrecked the layout whenever a promotion was live.
  const showSharedHeader = indexLayout.style !== 3;
  // Directory (1): header, then banner, then filter bar.
  const headerAboveBanner = indexLayout.style === 1;
  // Panel (2): banner leads, then the header sits in a white card above
  // the filter bar — the organizations panel structure.
  const headerInPanelCard = indexLayout.style === 2;
  // Style 3 with a live banner: the banner REPLACES the green hero, so
  // the title and create buttons would disappear with it. Render them
  // directly above the filter bar instead — the banner already occupies
  // the top of the page.
  const headerAboveFilterBar = indexLayout.style === 3 && hasFeaturedBanner;

  // Create Event + From buttons. `onGreen` styles them for the green hero
  // (white border/text); otherwise the original green-on-light styling.
  const renderAdminButtons = onGreen => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Button, {
      variant: "contained",
      onClick: handleCreateClick,
      sx: {
        backgroundColor: green.primary,
        color: common.white,
        // White outline only on the green hero so the button stands
        // out; the light-background fallback keeps its plain style.
        border: onGreen ? `2px solid ${common.white}` : "none",
        "&:hover": {
          backgroundColor: green.dark
        },
        px: 2
      },
      children: "Create Event"
    }), user?.role === "admin" && indexLayout.showUpload && /*#__PURE__*/_jsx(Button, {
      variant: "outlined",
      onClick: () => {
        router.push("/events/create?openDataTable=1");
      },
      endIcon: /*#__PURE__*/_jsx(TableChartIcon, {})
      // Same destination as the header's "Add by CSV" — the
      // legacy table importer.
      ,

      sx: {
        borderColor: onGreen ? common.white : green.primary,
        color: onGreen ? common.white : green.primary,
        "&:hover": {
          borderColor: onGreen ? common.white : green.dark,
          backgroundColor: onGreen ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.03)"
        },
        px: 2,
        ml: 1
      },
      children: "Add by CSV"
    })]
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [issues && issues.length > 0 && /*#__PURE__*/_jsx(Box, {
      role: "dialog",
      "aria-modal": "true",
      sx: {
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(0,0,0,0.35)",
        display: issuesOpen ? "flex" : "none",
        alignItems: "center",
        justifyContent: "center",
        zIndex: t => t.zIndex.modal
      },
      onClick: () => setIssuesOpen(false),
      children: /*#__PURE__*/_jsxs(Box, {
        onClick: e => e.stopPropagation(),
        sx: {
          width: "min(92vw, 720px)",
          maxHeight: "80vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 6,
          p: 3
        },
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "h6",
          gutterBottom: true,
          children: "Some events could not be exported"
        }), /*#__PURE__*/_jsx(Typography, {
          variant: "body2",
          color: "text.secondary",
          gutterBottom: true,
          children: "The following items contained invalid or missing fields and were excluded:"
        }), /*#__PURE__*/_jsx(Box, {
          component: "ul",
          sx: {
            pl: 3,
            mb: 2
          },
          children: issues.map((i, idx) => /*#__PURE__*/_jsx("li", {
            children: /*#__PURE__*/_jsxs(Typography, {
              variant: "body2",
              children: [i.key, ": ", i.reason]
            })
          }, `${i.key}-${idx}`))
        }), /*#__PURE__*/_jsx(Box, {
          sx: {
            display: "flex",
            justifyContent: "flex-end"
          },
          children: /*#__PURE__*/_jsx(Button, {
            variant: "contained",
            onClick: () => setIssuesOpen(false),
            children: "Close"
          })
        })]
      })
    }), /*#__PURE__*/_jsxs(Head, {
      children: [/*#__PURE__*/_jsx("title", {
        children: "Events Directory | Your Company"
      }), /*#__PURE__*/_jsx("meta", {
        name: "description",
        content: "Discover upcoming events, workshops, conferences, and networking opportunities. Join industry experts and expand your professional network."
      }), /*#__PURE__*/_jsx("meta", {
        name: "keywords",
        content: "events, workshops, conferences, networking, professional development, business events"
      }), /*#__PURE__*/_jsx("meta", {
        property: "og:title",
        content: "Events Directory | Your Company"
      }), /*#__PURE__*/_jsx("meta", {
        property: "og:description",
        content: "Discover upcoming events, workshops, conferences, and networking opportunities."
      }), /*#__PURE__*/_jsx("meta", {
        property: "og:type",
        content: "website"
      }), /*#__PURE__*/_jsx("meta", {
        property: "og:url",
        content: "https://yourdomain.com/events"
      }), /*#__PURE__*/_jsx("meta", {
        name: "twitter:card",
        content: "summary_large_image"
      }), /*#__PURE__*/_jsx("meta", {
        name: "twitter:title",
        content: "Events Directory | Your Company"
      }), /*#__PURE__*/_jsx("meta", {
        name: "twitter:description",
        content: "Discover upcoming events, workshops, conferences, and networking opportunities."
      }), /*#__PURE__*/_jsx("link", {
        rel: "canonical",
        href: "https://yourdomain.com/events"
      }), /*#__PURE__*/_jsx("script", {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Events Directory",
          description: "Directory of upcoming events, workshops, and conferences",
          numberOfItems: events.length,
          itemListElement: events.map((event, index) => ({
            "@type": "Event",
            position: index + 1,
            name: event.header,
            description: event.description,
            startDate: event.date,
            url: `https://yourdomain.com/events/${event.id || event.slug}`,
            image: `https://yourdomain.com${event.image}`,
            organizer: {
              "@type": "Organization",
              name: event.organizer?.name || "Unknown"
            },
            location: event.isVirtual ? {
              "@type": "VirtualLocation",
              url: "https://yourdomain.com"
            } : event.location ? {
              "@type": "Place",
              name: event.location.city
            } : undefined
          }))
        })
      })]
    }), /*#__PURE__*/_jsx(MainContainer, {
      sx: panelPageSx(indexLayout.style, green.primary),
      children: /*#__PURE__*/_jsxs(ContentWrapper, {
        children: [/*#__PURE__*/_jsx(NavBar, {}), headerAboveBanner && /*#__PURE__*/_jsx(Container, {
          maxWidth: "lg",
          sx: {
            px: 4,
            pt: 4
          },
          children: /*#__PURE__*/_jsx(IndexPageHeader, {
            style: indexLayout.style,
            accent: {
              primary: green.primary,
              dark: green.dark
            },
            title: "Events Directory",
            subtitle: "Discover upcoming events, workshops, and networking opportunities",
            ariaLabel: "Events Directory Header",
            isAdmin: canCreateEvent,
            create: true ? {
              label: "Create Event",
              onClick: handleCreateClick
            } : undefined,
            upload: indexLayout.showUpload ? {
              label: "Add by CSV",
              onClick: () => router.push("/events/create?openDataTable=1"),
              adminOnly: true
            } : undefined
          })
        }), loadingFeatured ? /*#__PURE__*/_jsx(Container
        // Full-bleed only under the legacy banner
        // style; the other styles constrain the
        // promoted section to the content width, the
        // way the organizations featured section is.
        , {
          maxWidth: indexLayout.style === 3 ? false : "lg",
          disableGutters: indexLayout.style === 3,
          sx: indexLayout.style === 3 ? undefined : {
            px: 4
          },
          children: /*#__PURE__*/_jsx(SingleFeaturedBannerSkeleton, {
            accentColor: green.primary,
            hideImageMobile: true
          })
        }) : featuredEventItems.length > 0 ? /*#__PURE__*/_jsx(Container
        // Full-bleed only under the legacy banner
        // style; the other styles constrain the
        // promoted section to the content width, the
        // way the organizations featured section is.
        , {
          maxWidth: indexLayout.style === 3 ? false : "lg",
          disableGutters: indexLayout.style === 3,
          sx: indexLayout.style === 3 ? undefined : {
            px: 4
          },
          children: /*#__PURE__*/_jsx(SingleFeaturedBanner, {
            items: featuredEventItems,
            accentColor: green.primary,
            accentDark: green.dark,
            ctaLabel: "View Event",
            sectionLabel: "Featured Event",
            onCta: item => router.push(item.link),
            dataSelectTarget: "Events Featured Banner",
            hideImageMobile: true
          })
        }) : showHeroBox ? /*#__PURE__*/_jsxs(Box, {
          "data-select-target": "Events Hero",
          sx: {
            backgroundColor: green.primary,
            py: 8,
            color: common.white,
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative"
          },
          children: [/*#__PURE__*/_jsx(Container, {
            children: /*#__PURE__*/_jsxs(Box, {
              sx: {
                p: 4
              },
              children: [/*#__PURE__*/_jsx(Typography, {
                variant: "h2",
                component: "h1",
                gutterBottom: true,
                children: "Events Directory"
              }), /*#__PURE__*/_jsx(Typography, {
                variant: "h5",
                component: "p",
                children: "Discover upcoming events, workshops, and networking opportunities"
              })]
            })
          }), /*#__PURE__*/_jsx(Box, {
            sx: {
              position: "absolute",
              bottom: {
                xs: 12,
                md: 16
              },
              right: {
                xs: 12,
                md: 16
              },
              display: "flex",
              alignItems: "center"
            },
            children: renderAdminButtons(true)
          }), (hasPendingForUser || hasRejectedForUser) && /*#__PURE__*/_jsx(Box, {
            sx: {
              position: "absolute",
              bottom: {
                xs: -6,
                md: -4
              },
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              px: 2
            },
            children: /*#__PURE__*/_jsx(Button, {
              variant: "text",
              color: hasPendingForUser ? "info" : "error",
              size: "small",
              onClick: () => setRejectedModalOpen(true),
              sx: {
                textTransform: "none",
                fontWeight: 600,
                p: 0,
                minHeight: "auto",
                color: common.white,
                textShadow: "0 1px 3px rgba(0,0,0,.6)"
              },
              children: submissionStatusLabel
            })
          })]
        }) : null, /*#__PURE__*/_jsxs(StyledContainer, {
          component: "main",
          id: MAIN_CONTENT_ID,
          tabIndex: -1,
          maxWidth: indexLayout.style === 3 ? false : "lg"
          // Directory/panel styles use the organizations page's
          // even spacing(4) gutters instead of the hero page's
          // narrower asymmetric padding.
          ,
          sx: indexLayout.style === 3 ? undefined : {
            px: 4,
            py: 4
          },
          children: [headerInPanelCard && /*#__PURE__*/_jsx(IndexPanelSurface, {
            style: 2,
            children: /*#__PURE__*/_jsx(IndexPageHeader, {
              style: 2,
              accent: {
                primary: green.primary,
                dark: green.dark
              },
              title: "Events Directory",
              subtitle: "Discover upcoming events, workshops, and networking opportunities",
              ariaLabel: "Events Directory Header",
              isAdmin: canCreateEvent,
              create: true ? {
                label: "Create Event",
                onClick: handleCreateClick
              } : undefined,
              upload: indexLayout.showUpload ? {
                label: "Add by CSV",
                onClick: () => router.push("/events/create?openDataTable=1"),
                adminOnly: true
              } : undefined
            })
          }), headerAboveFilterBar && /*#__PURE__*/_jsx(IndexPageHeader
          // Directory treatment: a solid hero here
          // would compete with the banner above it.
          , {
            style: 1,
            accent: {
              primary: green.primary,
              dark: green.dark
            },
            title: "Events Directory",
            subtitle: "Discover upcoming events, workshops, and networking opportunities",
            ariaLabel: "Events Directory Header",
            isAdmin: canCreateEvent,
            create: true ? {
              label: "Create Event",
              onClick: handleCreateClick
            } : undefined,
            upload: indexLayout.showUpload ? {
              label: "Add by CSV",
              onClick: () => router.push("/events/create?openDataTable=1"),
              adminOnly: true
            } : undefined
          }), useSharedFilterBar && /*#__PURE__*/_jsx(FilterBar, {
            accentColor: green.primary,
            selectTarget: "Events Filters",
            searchSubjectLabel: "Events",
            viewMode: eventViewMode,
            onViewModeChange: setEventViewMode,
            search: true,
            searchPlaceholder: "Search events...",
            searchValue: eventSearch,
            onSearchChange: setEventSearch,
            searchScope: "events",
            showPrioritizeSaved: true,
            prioritizeSaved: prioritizeSaved,
            onPrioritizeSavedChange: setPrioritizeSaved,
            extraToggle: {
              label: "Show Past Events",
              checked: showPastEvents,
              onChange: setShowPastEvents
            },
            filter1: {
              name: "Type",
              pluralName: "Types",
              // The chip toggles become a select —
              // options derived from the loaded events.
              options: categories.filter(c => c !== "all"),
              value: selectedCategory === "all" ? "" : selectedCategory,
              onChange: v => handleCategoryFilter(v || "all")
            },
            filter2: {
              name: "Format",
              pluralName: "Formats",
              options: ["In-person", "Virtual", "Hybrid"],
              value: eventFormat === "all" ? "" : eventFormat === "in-person" ? "In-person" : eventFormat === "virtual" ? "Virtual" : "Hybrid",
              onChange: v => setEventFormat(!v ? "all" : v === "In-person" ? "in-person" : v.toLowerCase())
            },
            filter3: {
              name: "Sort",
              hideAll: true,
              options: ["Date", "A–Z", "Z–A"],
              value: sortBy === "az" ? "A–Z" : sortBy === "za" ? "Z–A" : "Date",
              onChange: v => setSortBy(v === "A–Z" ? "az" : v === "Z–A" ? "za" : "date")
            }
          }), useSharedFilterBar && !showHeroBox && !showSharedHeader && !headerAboveFilterBar && /*#__PURE__*/_jsx(Box, {
            sx: {
              mb: 3,
              display: "flex"
            },
            children: renderAdminButtons(false)
          }), /*#__PURE__*/_jsx(Box, {
            "data-select-target": "Events Category Filters",
            sx: {
              mb: 4,
              display: useSharedFilterBar ? "none" : "block"
            },
            children: /*#__PURE__*/_jsx(Box, {
              sx: {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 2,
                flexWrap: "wrap"
              },
              children: /*#__PURE__*/_jsxs(Box, {
                children: [/*#__PURE__*/_jsx(Box, {
                  sx: {
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 1
                  },
                  children: (hasPendingForUser || hasRejectedForUser) && /*#__PURE__*/_jsx(Button, {
                    variant: "text",
                    color: hasPendingForUser ? "info" : "error",
                    size: "small",
                    onClick: () => setRejectedModalOpen(true),
                    sx: {
                      textTransform: "none",
                      fontWeight: 600
                    },
                    children: submissionStatusLabel
                  })
                }), /*#__PURE__*/_jsxs(Box, {
                  sx: {
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1
                  },
                  children: [categories.map((category, idx) => {
                    const key = String(category || "").toLowerCase();
                    const color = key === "all" ? gray.primary : categoryColors[key] || categoryColors.default;
                    const isSelected = selectedCategory === category;
                    return /*#__PURE__*/_jsx(Chip, {
                      label: category === "all" ? "All Events" : category,
                      onClick: () => handleCategoryFilter(category),
                      variant: isSelected ? "filled" : "outlined",
                      sx: {
                        cursor: "pointer",
                        fontWeight: 700,
                        ...(isSelected ? {
                          bgcolor: color,
                          color: common.white,
                          "&:hover": {
                            bgcolor: color,
                            opacity: 0.9
                          }
                        } : {
                          borderColor: color,
                          color: color,
                          backgroundColor: common.white,
                          "&:hover": {
                            backgroundColor: common.white,
                            opacity: 0.95
                          }
                        })
                      }
                    }, `${key || "uncat"}-${idx}`);
                  }), /*#__PURE__*/_jsx(TextField, {
                    size: "small",
                    placeholder: "Search events...",
                    value: eventSearch,
                    onChange: e => setEventSearch(e.target.value),
                    inputProps: {
                      "aria-label": "Search events"
                    },
                    InputProps: {
                      startAdornment: /*#__PURE__*/_jsx(InputAdornment, {
                        position: "start",
                        children: /*#__PURE__*/_jsx(SearchIcon, {
                          fontSize: "small"
                        })
                      })
                    },
                    sx: {
                      minWidth: 220,
                      ml: "20px",
                      backgroundColor: "#fff",
                      borderRadius: 1
                    }
                  }), /*#__PURE__*/_jsxs(Box, {
                    sx: {
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      ml: "20px"
                    },
                    children: [/*#__PURE__*/_jsx(Typography, {
                      variant: "body2",
                      sx: {
                        fontWeight: 600,
                        whiteSpace: "nowrap"
                      },
                      children: "Sort By"
                    }), /*#__PURE__*/_jsxs(Select, {
                      size: "small",
                      value: sortBy,
                      onChange: e => setSortBy(e.target.value),
                      sx: {
                        minWidth: 110
                      },
                      children: [/*#__PURE__*/_jsx(MenuItem, {
                        value: "date",
                        children: "Date"
                      }), /*#__PURE__*/_jsx(MenuItem, {
                        value: "az",
                        children: "A \u2013 Z"
                      }), /*#__PURE__*/_jsx(MenuItem, {
                        value: "za",
                        children: "Z \u2013 A"
                      })]
                    })]
                  }), /*#__PURE__*/_jsxs(Box, {
                    "data-select-target": "Event Format Filter",
                    sx: {
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      ml: "20px"
                    },
                    children: [/*#__PURE__*/_jsx(Typography, {
                      variant: "body2",
                      sx: {
                        fontWeight: 600,
                        whiteSpace: "nowrap"
                      },
                      children: "Event Format"
                    }), /*#__PURE__*/_jsxs(Select, {
                      size: "small",
                      value: eventFormat,
                      onChange: e => setEventFormat(e.target.value),
                      sx: {
                        minWidth: 120
                      },
                      children: [/*#__PURE__*/_jsx(MenuItem, {
                        value: "all",
                        children: "All"
                      }), /*#__PURE__*/_jsx(MenuItem, {
                        value: "in-person",
                        children: "In-person"
                      }), /*#__PURE__*/_jsx(MenuItem, {
                        value: "virtual",
                        children: "Virtual"
                      }), /*#__PURE__*/_jsx(MenuItem, {
                        value: "hybrid",
                        children: "Hybrid"
                      })]
                    })]
                  }), /*#__PURE__*/_jsx(PrioritizeSavedSwitch, {
                    show: hasSavedEvents,
                    checked: prioritizeSaved,
                    onChange: setPrioritizeSaved,
                    accentColor: green.primary,
                    sx: {
                      ml: 0.5,
                      mr: 0
                    }
                  }), /*#__PURE__*/_jsx(FormControlLabel, {
                    control: /*#__PURE__*/_jsx(Checkbox, {
                      checked: showPastEvents,
                      onChange: e => setShowPastEvents(e.target.checked),
                      size: "small",
                      sx: {
                        color: gray.dark,
                        "&.Mui-checked": {
                          color: gray.dark
                        }
                      }
                    }),
                    label: "Show Past Events",
                    sx: {
                      ml: 0.5,
                      mr: 0
                    }
                  }), !showHeroBox && !showSharedHeader && !headerAboveFilterBar && /*#__PURE__*/_jsxs(_Fragment, {
                    children: [/*#__PURE__*/_jsx(Box, {
                      sx: {
                        flex: "1 1 0",
                        minWidth: "50px"
                      }
                    }), renderAdminButtons(false)]
                  })]
                })]
              })
            })
          }), /*#__PURE__*/_jsx(Divider, {
            sx: {
              mb: 4
            }
          }), /*#__PURE__*/_jsxs(Box, {
            "data-select-target": "Events Card Grid",
            sx: eventViewMode === "list" ? {
              display: "flex",
              flexDirection: "column",
              rowGap: {
                xs: 2,
                md: 2.5
              },
              width: "100%",
              mx: "auto"
            } : {
              display: "flex",
              flexWrap: "wrap",
              columnGap: {
                xs: 2,
                md: 2.5
              },
              rowGap: {
                xs: 2.5,
                md: 3
              },
              justifyContent: {
                xs: "center",
                md: "center"
              },
              width: "100%",
              mx: "auto"
            },
            children: [showSkeletons && Array.from({
              length: skeletonCount
            }).map((_, i) => /*#__PURE__*/_jsx(EventCardSkeleton, {}, `skel-${i}`)), visibleEvents.filter(e => isFinite(e.price) && typeof e.slug === "string" && e.slug).map(event => renderEventCard(event))]
          }), !showSkeletons && filteredEvents.length === 0 && /*#__PURE__*/_jsxs(Box, {
            sx: {
              textAlign: "center",
              py: 8
            },
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "h6",
              color: "text.secondary",
              gutterBottom: true,
              children: "No events found"
            }), /*#__PURE__*/_jsx(Typography, {
              variant: "body2",
              color: "text.secondary",
              children: "Try selecting a different category or check back later for new events."
            })]
          }), !showSkeletons && displayEvents.length === 0 && filteredEvents.length > 0 && !showPastEvents && /*#__PURE__*/_jsxs(Box, {
            sx: {
              textAlign: "center",
              py: 8
            },
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "h6",
              color: "text.secondary",
              gutterBottom: true,
              children: "No upcoming events found"
            }), /*#__PURE__*/_jsx(Typography, {
              variant: "body2",
              color: "text.secondary",
              children: "Enable \u201CShow Past Events\u201D to view previous events."
            })]
          })]
        }), /*#__PURE__*/_jsx(SubscriptionBanner, {}), /*#__PURE__*/_jsxs(Dialog, {
          open: rejectedModalOpen,
          onClose: () => setRejectedModalOpen(false),
          maxWidth: "sm",
          fullWidth: true,
          children: [/*#__PURE__*/_jsx(DialogTitle, {
            children: hasPendingForUser ? "Submission pending review" : "Review current submission?"
          }), /*#__PURE__*/_jsxs(DialogContent, {
            dividers: true,
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "body2",
              color: "text.secondary",
              children: hasPendingForUser ? "Your event submission is currently pending review. You can review the submission or start a new one if needed." : "We found a rejected event submission tied to your account. You can review the feedback and resubmit, or start a brand-new submission."
            }), hasPendingForUser ? /*#__PURE__*/_jsx(Alert, {
              severity: "info",
              sx: {
                mt: 2
              },
              children: "No messages yet. Your submission is queued for review."
            }) : latestAdminMessage?.body ? /*#__PURE__*/_jsxs(Alert, {
              severity: "error",
              sx: {
                mt: 2
              },
              children: [/*#__PURE__*/_jsx("strong", {
                children: "Rejection message:"
              }), " ", latestAdminMessage.body]
            }) : /*#__PURE__*/_jsx(Alert, {
              severity: "info",
              sx: {
                mt: 2
              },
              children: "No rejection message was provided. You can still review and resubmit your submission."
            })]
          }), /*#__PURE__*/_jsxs(DialogActions, {
            children: [/*#__PURE__*/_jsx(Button, {
              onClick: () => setRejectedModalOpen(false),
              children: "Cancel"
            }), (hasPendingForUser || hasRejectedForUser) && /*#__PURE__*/_jsx(Button, {
              variant: "outlined",
              color: "error",
              onClick: handleDiscardSubmission,
              disabled: !activeSubmission,
              children: "Discard submission"
            }), /*#__PURE__*/_jsx(Button, {
              variant: "outlined",
              onClick: handleCreateNew,
              children: "Create new"
            }), /*#__PURE__*/_jsx(Button, {
              variant: "contained",
              onClick: handleReviewRejected,
              disabled: !activeSubmission,
              children: "Review current submission"
            })]
          })]
        }), /*#__PURE__*/_jsx(SectionFooter, {})]
      })
    })]
  });
};
export const getStaticProps = async () => {
  try {
    const {
      events,
      issues
    } = await fetchAllEventsWithIssues();
    return {
      props: {
        events,
        issues
      }
    };
  } catch (error) {
    console.error("Error fetching events:", error);
    return {
      props: {
        events: [],
        issues: []
      }
    };
  }
};
export default EventsPage;